import pluralize from 'pluralize';
import { useCallback } from 'react';

import { type MentionItem, useChatComposerNodeStore, useChatComposerStore } from '@air/feature-ag-ui-chat';
import { useToasts } from '@air/provider-toast';

import { getVideoUploadRejectionMessage } from '../utils/canvasVideoUploadLimits';
import { type ClassifiedUploadFile, classifyAcceptedUploadFiles } from '../utils/classifyAcceptedUploadFiles';
import { getCompatibleImageUrl } from '../utils/getCompatibleImage';
import { getVideoThumbnailUrl } from '../utils/getVideoThumbnailUrl';
import { partitionDroppedUploadFiles } from '../utils/uploadMimeTypes';
import { useUploadAndPreload } from './useUploadAndPreload';

type ChatUploadItem = ClassifiedUploadFile;

const getSuccessToastMessage = (imageCount: number, videoCount: number): string => {
  const total = imageCount + videoCount;

  if (total === 1) {
    return videoCount === 1 ? 'Video added to chat' : 'Image added to chat';
  }

  if (imageCount > 0 && videoCount > 0) {
    return `${total} items added to chat`;
  }

  if (videoCount > 0) {
    return `${total} videos added to chat`;
  }

  return `${total} images added to chat`;
};

/**
 * Returns a callback that accepts raw File objects, uploads them to S3,
 * and inserts them as mention tokens in the chat composer.
 *
 * Follows the same token insertion pattern as {@link ChatDropTarget} but
 * uploads files first (since they come from the OS, not the workspace).
 */
export const useDropFilesToChat = () => {
  const { showToast } = useToasts();
  const { uploadAndPreload } = useUploadAndPreload();

  const dropFilesToChat = useCallback(
    async (files: File[]) => {
      const { accepted, unsupportedCount } = partitionDroppedUploadFiles(files);
      const { uploadFiles, videoRejections } = await classifyAcceptedUploadFiles(accepted);

      if (uploadFiles.length === 0) {
        if (videoRejections.length > 0) {
          showToast(getVideoUploadRejectionMessage(videoRejections), { color: 'red' });
        } else {
          showToast('No supported images or videos selected', { color: 'red' });
        }

        return;
      }

      useChatComposerStore.getState().setIsUploadingToChat(true);

      try {
        const uploadItems: ChatUploadItem[] = uploadFiles;

        const results = await uploadAndPreload(
          uploadItems.map((item) => ({ contentType: item.contentType, file: item.file })),
        );

        // Read insertToken fresh AFTER the upload. The composer registers it a tick after mount
        // (the editor builds asynchronously, and the hero composer remounts when the chat is
        // toggled), so an up-front check can spuriously fail; by upload-completion it's registered.
        const insertToken = useChatComposerStore.getState().insertToken;

        if (!insertToken) {
          showToast('Chat composer is not available', { color: 'red' });
          return;
        }

        let imageAddedCount = 0;
        let videoAddedCount = 0;
        let uploadFailCount = 0;

        for (let i = 0; i < uploadItems.length; i++) {
          const item = uploadItems[i];
          const result = results[i];

          if (result.status !== 'fulfilled') {
            uploadFailCount++;
            continue;
          }

          const cdnUrl = result.cdnUrl;
          const id = crypto.randomUUID();

          if (item.mediaKind === 'video') {
            const thumbnailUrl = getVideoThumbnailUrl(cdnUrl);
            const mentionItem: MentionItem = {
              id,
              name: item.file.name,
              thumbnailUrl,
              type: 'video',
            };
            useChatComposerNodeStore.getState().addNode(id, {
              fileName: item.file.name,
              group: { id },
              media: { image: thumbnailUrl, original: cdnUrl },
              type: 'video',
            });
            insertToken(mentionItem);
            videoAddedCount++;
          } else {
            const imageUrl = getCompatibleImageUrl(cdnUrl);
            const mentionItem: MentionItem = {
              id,
              name: item.file.name,
              thumbnailUrl: imageUrl,
              type: 'image',
            };
            useChatComposerNodeStore.getState().addNode(id, {
              fileName: item.file.name,
              group: { id },
              media: { image: imageUrl, original: cdnUrl },
              type: 'image',
            });
            insertToken(mentionItem);
            imageAddedCount++;
          }
        }

        const addedCount = imageAddedCount + videoAddedCount;
        const totalFailed = unsupportedCount + uploadFailCount;

        if (addedCount === 0) {
          showToast('Failed to upload media', { color: 'red' });
        } else if (totalFailed > 0) {
          showToast(`${pluralize('file', totalFailed, true)} couldn't be added`, { color: 'red' });
        } else {
          showToast(getSuccessToastMessage(imageAddedCount, videoAddedCount));
        }

        if (videoRejections.length > 0) {
          showToast(getVideoUploadRejectionMessage(videoRejections), { color: 'red' });
        }
      } finally {
        for (const item of uploadFiles) {
          if (item.mediaKind === 'video') {
            URL.revokeObjectURL(item.blobUrl);
          }
        }
        useChatComposerStore.getState().setIsUploadingToChat(false);
      }
    },
    [showToast, uploadAndPreload],
  );

  return { dropFilesToChat };
};
